import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { OUTCOME_TEMPLATES } from "../../../shared/outcomeTemplates";
import { pathScreens } from "../../../shared/tour";
import { api } from "../../api";
import { IconEdit, IconPlus, IconTrash } from "../../icons";
import { useEditor } from "../DemoEditor";

/**
 * The path being edited, top of the Studio's screen list, always shown: the default path and every other path (each
 * its own list of screens), with New path, Rename and Delete. `onChange` is asked before switching, so the open screen
 * can be saved first.
 */
export function PathBar({ value, onChange }: { value: string | null; onChange: (v: string | null) => void | Promise<void> }) {
  const { bundle, setBundle } = useEditor();
  const [editing, setEditing] = useState<null | "new" | "rename">(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const current = value ? bundle.outcomes.find((o) => o.id === value) : undefined;
  useEffect(() => { if (editing) input.current?.focus(); }, [editing]);

  const unused = OUTCOME_TEMPLATES.filter((t) => !bundle.outcomes.some((o) => o.name.toLowerCase() === t.name.toLowerCase()));
  const start = (what: "new" | "rename") => { setError(null); setName(what === "rename" ? current?.name ?? "" : ""); setEditing(what); };
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); setEditing(null); } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    if (editing === "new") {
      const template = OUTCOME_TEMPLATES.find((t) => t.name.toLowerCase() === n.toLowerCase());
      void run(async () => {
        const r = await api.addPath(bundle.demo.id, { name: n, templateKey: template?.key ?? null });
        setBundle(r.bundle);
        await onChange(r.id);
      });
    } else if (current) {
      void run(async () => setBundle(await api.updatePath(bundle.demo.id, current.id, { name: n })));
    }
  };
  const remove = () => {
    if (!current) return;
    const n = pathScreens(bundle, current.id).length;
    if (!confirm(`Delete the path "${current.name}" and its ${n} ${n === 1 ? "screen" : "screens"}? Other paths keep their own. Viewers it would have routed get the default path.`)) return;
    void run(async () => {
      await onChange(null);
      setBundle(await api.deletePath(bundle.demo.id, current.id));
    });
  };

  if (editing) {
    return (
      <form className="path-bar path-edit" onSubmit={submit}>
        <label className="path-select">
          <span className="eyebrow">{editing === "new" ? "New path" : "Rename path"}</span>
          <input ref={input} value={name} maxLength={120} list="path-name-ideas" placeholder="e.g. Reduce cost" aria-label="Path name"
            onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setEditing(null); } }} />
          <datalist id="path-name-ideas">{unused.map((t) => <option key={t.key} value={t.name}>{t.description}</option>)}</datalist>
        </label>
        <div className="path-actions">
          <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !name.trim()}>{editing === "new" ? "Create" : "Rename"}</button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
        </div>
        {editing === "new" && <p className="small muted path-note">Starts with copies of the default path's screens, to change on this path only.</p>}
        {error && <div className="error-box">{error}</div>}
      </form>
    );
  }
  return (
    <div className="path-bar">
      <label className="path-select">
        <span className="eyebrow">Path</span>
        <select value={value ?? ""} onChange={(e) => void onChange(e.target.value || null)} aria-label="Path">
          <option value="">Default path ({pathScreens(bundle, null).length})</option>
          {bundle.outcomes.map((o) => <option key={o.id} value={o.id}>{o.name} ({pathScreens(bundle, o.id).length})</option>)}
        </select>
      </label>
      <div className="path-actions">
        <button type="button" className="btn btn-sm btn-ghost btn-icon" onClick={() => start("new")} aria-label="New path" title="New path"><IconPlus /></button>
        <button type="button" className="btn btn-sm btn-ghost btn-icon" onClick={() => start("rename")} disabled={!current} aria-label="Rename path" title={current ? "Rename this path" : "The default path can't be renamed"}><IconEdit /></button>
        <button type="button" className="btn btn-sm btn-ghost btn-icon" onClick={remove} disabled={!current || busy} aria-label="Delete path" title={current ? "Delete this path" : "The default path can't be deleted"}><IconTrash /></button>
      </div>
      {current && (
        <p className="small muted path-note">
          {current.tags.length ? <>Viewers tagged {current.tags.map((t) => `#${t}`).join(", ")} are routed here.</> : <>No routing tags yet: set them under Paths.</>}
        </p>
      )}
      {error && <div className="error-box">{error}</div>}
    </div>
  );
}

export function usePathParam(): [string | null, (v: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const { bundle } = useEditor();
  const raw = params.get("path");
  const value = raw && bundle.outcomes.some((o) => o.id === raw) ? raw : null;
  return [value, (v) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set("path", v); else n.delete("path"); n.delete("step"); return n; }, { replace: true })];
}
